import SearchIcon from '@mui/icons-material/Search';
import { Box, Divider, Grid, InputAdornment, TextField } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IsSmallScreen } from '../../commonFunctions/CommonFunctions';

interface IFilterSearchTextProps {
    rows: IRows;
    onFilterChange: (filters: IFilter[]) => void;
    alignment?: 'left' | 'center' | 'right';
    defaultFilter?: any[]
    placeholder?: string
}

export interface IRows {
    fieldName?: string;
}

interface IFilter {
    field: string;
    operator: Operator | string;
    value: string | number;
}

export enum Operator {
    EqualTo = 'eq',
    NotEqualTo = 'neq',
    GreaterThan = 'gt',
    LessThan = 'lt',
    Like = 'like',
}

export const FilterSearchTextNew: React.FC<IFilterSearchTextProps> = React.memo(({ rows, onFilterChange, alignment = 'left', defaultFilter, placeholder = 'Search' }) => {
    const methods = useForm();
    const { register, handleSubmit, control, reset, watch } = methods;
    const isMobile = IsSmallScreen();
    const searchText: string | undefined = watch('searchAll');
    const lastAppliedRef = useRef<string>('');

    const handleReset = () => {
        reset(); // ✅ Correct way to reset form
        onFilterChange([]); // Clear filters
    };

    const onSubmit = (data: Record<string, any>) => {
        const term = (data.searchAll ?? '').trim();
        if (term === lastAppliedRef.current) return; // avoid re-requesting the same search
        lastAppliedRef.current = term;

        if (!rows || !rows.fieldName) {
            console.warn("No field names found in rows.");
            return;
        }

        // ✅ Correct usage of `reduce()`
        // const appliedFilters: any[] = (rows.fieldName || []).reduce((acc: any[], field) => {
        //     if (data.searchAll!== "") { // Ensure valid input
        //         acc.push({
        //             field: rows.fieldName,
        //             operator: Operator.Like, // ✅ Use `Operator.EqualTo` if needed
        //             value: data.searchAll , // ✅ Uses actual field value
        //         });
        //     }
        //     return acc;
        // }, []);
        const appliedFilters: any[] = []
        if (defaultFilter && defaultFilter.length > 0) {
            appliedFilters.push(...defaultFilter)
        }
        if (term !== "") {
            appliedFilters.push({
                field: rows.fieldName,
                operator: Operator.Like, // ✅ Use `Operator.EqualTo` if needed
                value: term,
            });
        }
        onFilterChange(appliedFilters);
    };

    // Search as the user types (debounced); Enter still searches immediately via the form submit.
    useEffect(() => {
        if (searchText === undefined) return; // nothing typed yet
        const timer = setTimeout(() => onSubmit({ searchAll: searchText }), 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);


    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Divider sx={{ marginLeft: 1 }} />

                <Grid container spacing={2} sx={{ margin: 1 }}>

                    <Grid item sm={12}>
                        <Box sx={{ flexGrow: 1 }}>
                            <TextField
                                id="searchAll"
                                size="small"
                                {...register('searchAll')}
                                name="searchAll"
                                label="" // Remove the floating label for a cleaner look
                                placeholder={placeholder}
                                title={placeholder} // full text on hover when the placeholder is truncated
                                variant="outlined"
                                inputProps={{ 'aria-label': placeholder }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: '100%',
                                    backgroundColor: '#F5F7FA',
                                    borderRadius: '25px', // pill shape
                                    
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '25px',
                                        paddingRight: 1,
                                        '& fieldset': {
                                            borderColor: '#098963', // light gray
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#9CA3AF', // darker on hover
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#3B82F6', // primary on focus
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '8px 14px',
                                        fontSize: '0.875rem',
                                    },
                                }}
                            />
                        </Box>

                    </Grid>



                </Grid>

            </form>
        </FormProvider>
    );
});
